import type { SupabaseClient } from '@supabase/supabase-js'
import type { AnalyticsEvent, Draft, Loop, Profile, Scene } from './types'
import { db, saveLocalMedia } from './db'

export interface UploadProgress { loaded: number; total: number }
export interface UploadResult { mediaUrl: string; posterUrl?: string }
export interface VideoStorageProvider {
  mode: 'demo' | 'supabase'
  upload(file: Blob, options?: { signal?: AbortSignal; onProgress?: (progress: UploadProgress) => void }): Promise<UploadResult>
  remove(mediaUrl: string): Promise<void>
}
export interface AuthProvider { currentUser(): Promise<Profile | null>; signOut(): Promise<void> }
export interface LoopRepository { feed(kind: 'for-you' | 'following' | 'fresh'): Promise<Loop[]>; publish(draft: Draft): Promise<Loop> }
export interface SocialGraphProvider { follow(profileId: string): Promise<void>; unfollow(profileId: string): Promise<void> }
export interface PlatformAuthProvider { connect(): Promise<{ connected: boolean }> }
export interface PlatformPublisher { publishLoop(loop: Loop, text?: string): Promise<{ externalId: string }> }
export interface PlatformTrendProvider { trends(): Promise<{ name: string; velocity: number; suggestedScene?: Scene }[]> }
export interface AnalyticsProvider { track(event: AnalyticsEvent): Promise<void> }
export interface DirectorProvider { analyze(draft: Draft): Promise<{ title: string; detail: string; confidence: number }[]> }

export const demoVideoStorage: VideoStorageProvider = {
  mode: 'demo',
  async upload(file) {
    const id = await saveLocalMedia(file, 'demo-upload')
    return { mediaUrl: `idb://${id}` }
  },
  async remove(mediaUrl) {
    if (mediaUrl.startsWith('idb://')) await db.media.delete(mediaUrl.slice(6))
  }
}

export const localAnalytics: AnalyticsProvider = {
  async track(event) { await db.events.add({ name: event.name, at: event.at, payload: event as unknown as Record<string, unknown> }) }
}

export const localDirector: DirectorProvider = {
  async analyze(draft) {
    const duration = draft.trimEndMs - draft.trimStartMs
    return [
      { title: duration > 5800 ? 'Tighten the exit' : 'Timing is compact', detail: duration > 5800 ? 'Try ending 180 ms earlier; the final beat will land harder.' : 'The current cut leaves room for the loop to breathe.', confidence: 0.84 },
      { title: 'Seam candidate', detail: 'The first and final frames share a similar center of motion.', confidence: 0.76 },
      { title: 'Alt text starter', detail: draft.caption ? `A six-second scene: ${draft.caption}` : 'Describe the action and the final transformation.', confidence: 0.92 }
    ]
  }
}

export async function getSupabaseClient(): Promise<SupabaseClient | null> {
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !key) return null
  const { createClient } = await import('@supabase/supabase-js')
  return createClient(url, key, { auth: { persistSession: true } })
}

export async function createSupabaseVideoStorageProvider(): Promise<VideoStorageProvider | null> {
  const client = await getSupabaseClient()
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!client || !url || !anonKey) return null
  return {
    mode: 'supabase',
    async upload(file, options) {
      const { data: { session } } = await client.auth.getSession()
      if (!session) throw new Error('Sign in before uploading across devices.')
      const extension = file.type.split('/')[1]?.replace('quicktime', 'mov') || 'bin'
      const path = `${session.user.id}/${crypto.randomUUID()}.${extension}`
      const endpoint = `${url}/storage/v1/object/loop-media/${path}`
      await new Promise<void>((resolve, reject) => {
        const request = new XMLHttpRequest()
        request.open('POST', endpoint)
        request.setRequestHeader('Authorization', `Bearer ${session.access_token}`)
        request.setRequestHeader('apikey', anonKey)
        request.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
        request.setRequestHeader('x-upsert', 'false')
        request.upload.onprogress = (event) => options?.onProgress?.({ loaded: event.loaded, total: event.total || file.size })
        request.onload = () => request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`Upload failed with status ${request.status}.`))
        request.onerror = () => reject(new Error('The upload connection failed.'))
        request.onabort = () => reject(new DOMException('Upload cancelled.', 'AbortError'))
        options?.signal?.addEventListener('abort', () => request.abort(), { once: true })
        request.send(file)
      })
      const { data } = client.storage.from('loop-media').getPublicUrl(path)
      return { mediaUrl: data.publicUrl }
    },
    async remove(mediaUrl) {
      const marker = '/loop-media/'
      const path = mediaUrl.includes(marker) ? mediaUrl.split(marker)[1] : undefined
      if (!path) throw new Error('That media URL does not belong to the configured Loop bucket.')
      const { error } = await client.storage.from('loop-media').remove([decodeURIComponent(path)])
      if (error) throw error
    }
  }
}

export const platformMocks = {
  auth: { async connect() { return { connected: true } } } satisfies PlatformAuthProvider,
  publisher: { async publishLoop(loop: Loop) { return { externalId: `host-${loop.id}` } } } satisfies PlatformPublisher,
  trends: { async trends() { return [{ name: 'THE FINAL EVERYONE SAW', velocity: 94 }, { name: 'TUESDAY WEATHER', velocity: 71 }, { name: 'NIGHT TRAIN', velocity: 63 }] } } satisfies PlatformTrendProvider
}
